import { HStack, VStack } from "@/components/Layout";
import { Text } from "@/components/Typography/Text";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useDebouncedCallback } from "@/hooks/useDebouncedCallback";
import useValueToLabelCache from "@/hooks/useValueToLabelCache";
import { defaultNS, useTranslation } from "@/lib/i18n";
import { genNameInitials } from "@/utils/name";
import { useState } from "react";
import Combobox from "../index";
import type { ComboboxOption, ComboboxOptionRenderer } from "../types";
import { useWorkersForSelect } from "./-query";

/**
 * DressCodeのPerson情報を表す型
 */
export type Person = Readonly<{
  id: string;
  name: string;
  nameInitials: string;
  email: string;
  avatarUrl: string;
}>;

interface PersonSelectorComboboxProps {
  onSelect: (person: Person) => void;
  disabledPersonIds?: Set<string>;
  onClose: () => void;
}

const renderPersonOption: ComboboxOptionRenderer<ComboboxOption> = ({
  option,
}) => (
  <HStack className="items-center gap-3">
    <Avatar className="size-6">
      <AvatarImage src={option.avatarUrl} alt={option.label} />
      <AvatarFallback>{genNameInitials(option.label)}</AvatarFallback>
    </Avatar>
    <VStack>
      <Text variant="labelSmall">{option.label}</Text>
      {option.email && (
        <Text variant="labelSmall" className="text-muted-foreground">
          {option.email}
        </Text>
      )}
    </VStack>
  </HStack>
);

export default function PersonSelectorCombobox({
  onSelect,
  disabledPersonIds = new Set(),
  onClose,
}: PersonSelectorComboboxProps) {
  const { t } = useTranslation(defaultNS, {
    keyPrefix: "component.Combobox.personSelectorCombobox",
  });

  const [searchText, setSearchText] = useState("");
  const [value, setValue] = useState<string | null>(null);
  const valueToLabelCache = useValueToLabelCache();

  const {
    data: options = [],
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    isFetching,
  } = useWorkersForSelect(searchText, disabledPersonIds);

  const handleValueChange = (newValue: string | null) => {
    if (!newValue) return;

    setValue(newValue);
    const selectedOption = options.find(
      (option: { value: string }) => option.value === newValue,
    );
    if (!selectedOption) return;

    onSelect({
      id: selectedOption.value,
      name: selectedOption.label,
      nameInitials: genNameInitials(selectedOption.label),
      email: selectedOption.email ?? "",
      avatarUrl: selectedOption.avatarUrl ?? "",
    });
    onClose();
  };

  const handleScrollToEnd = () => {
    if (hasNextPage && !isFetching) {
      fetchNextPage();
    }
  };

  return (
    <Combobox
      type="single"
      value={value}
      options={options}
      isFetching={isLoading || isFetchingNextPage}
      onValueChange={handleValueChange}
      searchPlaceholder={t("searchPersonPlaceholder")}
      onSearchChange={useDebouncedCallback((text) => setSearchText(text), [])}
      valueToLabelCache={valueToLabelCache}
      renderOption={renderPersonOption}
      onScrollToEnd={handleScrollToEnd}
    />
  );
}
